import {
  DOCUMENT,
  type EnvironmentProviders,
  InjectionToken,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
} from '@angular/core';

import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';

export const NBX_THEME_MODES = [
  'rentals',
  'ssr',
  'lock',
  'home',
  'service',
  'guest',
  'icon',
] as const;
export type NbxThemeMode = (typeof NBX_THEME_MODES)[number];

export const NBX_THEME_SCHEMES = ['light', 'dark'] as const;
export type NbxThemeScheme = (typeof NBX_THEME_SCHEMES)[number];

export interface NbxThemeConfig {
  mode: NbxThemeMode;
  scheme?: NbxThemeScheme;
  darkClass?: string;
}

export const NBX_THEME_CONFIG = new InjectionToken<NbxThemeConfig>('NBX_THEME_CONFIG', {
  factory: () => ({ mode: 'rentals' }),
});

const MODE_ATTRIBUTE = 'data-nbx-mode';
const SCHEME_ATTRIBUTE = 'data-nbx-scheme';
const BRAND_PROPERTY = /^--nbx-color-(primary|secondary)-(50|[1-9]00|950)$/;
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

interface NbxThemeState {
  mode: NbxThemeMode;
  scheme: NbxThemeScheme;
  brand: Record<string, string>;
}

/** Whether a value is one of the NBX modes. */
export function isNbxThemeMode(value: unknown): value is NbxThemeMode {
  return typeof value === 'string' && (NBX_THEME_MODES as readonly string[]).includes(value);
}

/** Whether a value is one of the NBX colour schemes. */
export function isNbxThemeScheme(value: unknown): value is NbxThemeScheme {
  return typeof value === 'string' && (NBX_THEME_SCHEMES as readonly string[]).includes(value);
}

/**
 * Runtime owner of the NBX theme: the active mode and colour scheme as signals, mirrored on
 * `<html data-nbx-mode data-nbx-scheme>`, plus the tenant brand overrides of the mode-aware
 * palettes. Changing the mode only swaps attributes and custom properties, never the PrimeNG
 * preset. The document is reached through DI, so the server render carries the same initial
 * attributes as the client and hydration sees no mismatch. Nothing is persisted.
 */
export const NbxThemeStore = signalStore(
  { providedIn: 'root' },
  withState<NbxThemeState>(() => {
    const config = inject(NBX_THEME_CONFIG);
    return { mode: config.mode, scheme: config.scheme ?? 'light', brand: {} };
  }),
  withMethods(store => {
    const root = inject(DOCUMENT).documentElement;
    const darkClass = inject(NBX_THEME_CONFIG).darkClass;

    return {
      /** Activates a mode; returns false and leaves the document untouched for an unknown one. */
      setMode(mode: NbxThemeMode): boolean {
        if (!isNbxThemeMode(mode)) {
          return false;
        }
        root.setAttribute(MODE_ATTRIBUTE, mode);
        patchState(store, { mode });
        return true;
      },

      /** Activates a colour scheme; returns false and leaves the document untouched when unknown. */
      setScheme(scheme: NbxThemeScheme): boolean {
        if (!isNbxThemeScheme(scheme)) {
          return false;
        }
        root.setAttribute(SCHEME_ATTRIBUTE, scheme);
        if (darkClass) {
          root.classList.toggle(darkClass, scheme === 'dark');
        }
        patchState(store, { scheme });
        return true;
      },

      /**
       * Replaces the tenant brand overrides. Only `--nbx-color-primary-*` and
       * `--nbx-color-secondary-*` with six-digit hex values are applied; anything else is
       * dropped so tenant data cannot restyle other tokens. Returns the applied properties.
       */
      setBrand(properties: Record<string, string>): Record<string, string> {
        const brand = Object.fromEntries(
          Object.entries(properties).filter(
            ([name, value]) => BRAND_PROPERTY.test(name) && HEX_COLOR.test(value)
          )
        );
        for (const name of Object.keys(store.brand())) {
          root.style.removeProperty(name);
        }
        for (const [name, value] of Object.entries(brand)) {
          root.style.setProperty(name, value);
        }
        patchState(store, { brand });
        return brand;
      },

      /** Removes every tenant brand override, back to the mode's own palettes. */
      clearBrand(): void {
        for (const name of Object.keys(store.brand())) {
          root.style.removeProperty(name);
        }
        patchState(store, { brand: {} });
      },
    };
  }),
  withHooks({
    onInit(store) {
      store.setMode(store.mode());
      store.setScheme(store.scheme());
    },
  })
);

/**
 * Sets the app's initial NBX mode and scheme and applies them to the document as soon as the
 * application starts, on the server and in the browser alike.
 */
export function provideNbxTheme(config: NbxThemeConfig): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: NBX_THEME_CONFIG, useValue: config },
    provideEnvironmentInitializer(() => inject(NbxThemeStore)),
  ]);
}
